import { Switch, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import en from '../i18n/en-us.json';
import pt from '../i18n/pt-br.json';
import es from '../i18n/es-es.json';
import { NotificationChannel, TenantSettingsKey, UpdateTenantViewModel } from '../model/tenant-notification';
import { useEffect, useState } from 'react';
import { TenantNotificationService } from '../services/tenant-notification.service';
import { Constants } from '../constants/constants';
import { useFormik } from 'formik';
import { toast } from "react-toastify";
import SaveButton from 'common/partials/SaveButton';
import i18next from 'i18next';
import * as Yup from 'yup';

export interface Props {
    tenantId: string;
}

interface FormContract {
    channelSettings: {
        [k in NotificationChannel]?: boolean
    };
    teamsWebhookUrl: string;
}

const defaultChannels: FormContract['channelSettings'] = {
    [NotificationChannel.App]: false,
    [NotificationChannel.Email]: false,
    [NotificationChannel.Teams]: false,
};

const keyToChannelMap: { [key in TenantSettingsKey]?: NotificationChannel } = {
    [TenantSettingsKey.StepExecutionAppNotificationEnabled]: NotificationChannel.App,
    [TenantSettingsKey.StepExecutionEmailNotificationEnabled]: NotificationChannel.Email,
    [TenantSettingsKey.StepExecutionTeamsNotificationEnabled]: NotificationChannel.Teams,
};

const channelToKeyMap: { [key in NotificationChannel]?: TenantSettingsKey } = {
    [NotificationChannel.App]: TenantSettingsKey.StepExecutionAppNotificationEnabled,
    [NotificationChannel.Email]: TenantSettingsKey.StepExecutionEmailNotificationEnabled,
    [NotificationChannel.Teams]: TenantSettingsKey.StepExecutionTeamsNotificationEnabled,
};

export function TenantNotificationSettings({ tenantId }: Props) {
    i18next.addResourceBundle('us', 'translation', en);
    i18next.addResourceBundle('br', 'translation', pt);
    i18next.addResourceBundle('es', 'translation', es);

    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);

    const [initialFormState, setInitialFormState] = useState<FormContract>({
        channelSettings: defaultChannels,
        teamsWebhookUrl: '',
    });

    const formik = useFormik<FormContract>({
        initialValues: initialFormState,
        validationSchema: Yup.object({
            teamsWebhookUrl: Yup.string().when('channelSettings', {
                is: (channels: FormContract['channelSettings']) => channels?.[NotificationChannel.Teams],
                then: (schema) => schema
                    .required(t('crud.validators.requiredField'))
                    .matches(/^https:\/\//, t('validation.uri')),
                otherwise: (schema) => schema.notRequired(),
            }),
        }),
        onSubmit: async (values) => {
            const promises: Promise<any>[] = [];

            Object.keys(values.channelSettings).forEach(ch => {
                const channel = Number(ch) as NotificationChannel;
                const key = channelToKeyMap[channel];

                if (key && values.channelSettings[channel] !== initialFormState.channelSettings[channel]) {
                    const payload: UpdateTenantViewModel = {
                        tenantSettingsKeyId: key,
                        value: values.channelSettings[channel]!
                    };
                    promises.push(TenantNotificationService.updateTenantSetting(tenantId, payload));
                }
            });

            if (values.teamsWebhookUrl !== initialFormState.teamsWebhookUrl) {
                const payload: UpdateTenantViewModel = {
                    tenantSettingsKeyId: TenantSettingsKey.StepExecutionTeamsNotificationWebhook,
                    value: values.teamsWebhookUrl
                };
                promises.push(TenantNotificationService.updateTenantSetting(tenantId, payload));
            }

            if (promises.length === 0) {
                toast.info(t('crud.noChanges') || "No changes to save");
                return;
            }

            setIsLoading(true);
            try {
                await Promise.all(promises);
                toast.success(t('crud.update.successMessage'));

                setInitialFormState(values);
                formik.resetForm({ values });
            } catch (e) {
                console.error("Failed to save tenant settings:", e);
                toast.error(t('crud.update.errorMessage'));
            } finally {
                setIsLoading(false);
            }
        },
        enableReinitialize: true,
    });

    useEffect(() => {
        const fetchTenantSettings = async () => {
            if (!tenantId) {
                setIsLoading(false);
                return;
            };

            setIsLoading(true);
            try {
                const response = await TenantNotificationService.getTenantSettings(tenantId);
                console.log(response!.data);

                if (response == null || response.hasErrors) {
                    toast.error(t('crud.read.errorMessage'));
                    return;

                }

                const settings = response.data;
                const newFormState: FormContract = {
                    channelSettings: { ...defaultChannels },
                    teamsWebhookUrl: '',
                };

                settings.forEach(setting => {
                    const { tenantSettingsKeyId, value } = setting;

                    if (tenantSettingsKeyId === TenantSettingsKey.StepExecutionTeamsNotificationWebhook) {
                        newFormState.teamsWebhookUrl = value as string;
                    } else {
                        const channel = keyToChannelMap[tenantSettingsKeyId];
                        newFormState.channelSettings[channel!] = value as boolean;
                    }
                });

                setInitialFormState(newFormState);
                formik.setValues(newFormState, false);
            } catch (e) {
                console.error("A critical error occurred during settings fetch:", e);
                toast.error(t('crud.read.errorMessage'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchTenantSettings();
    }, [tenantId]);

    useEffect(() => {
        if (!formik.values.channelSettings[NotificationChannel.Teams]) {
            formik.setFieldValue('teamsWebhookUrl', '');
            formik.setFieldTouched('teamsWebhookUrl', false);
        }
    }, [formik.values.channelSettings[NotificationChannel.Teams]]);

    if (isLoading && !formik.dirty) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className="card shadow-none">
            <form onSubmit={formik.handleSubmit} noValidate>
                <div className="card-body pt-5">
                    <h4>{t("notificationCenter.title")}</h4>

                    <label className='form-label mt-5 fw-bold'>{t("notificationCenter.channelsLabel")}</label>
                    <div className="row">
                        {
                            Object.values(NotificationChannel)
                                .filter(c => typeof c === 'number' && c !== NotificationChannel.None)
                                .map(c => c as NotificationChannel)
                                .map((c) =>
                                    <div key={c.toString()} className="col-md-4 col-lg-3 d-flex align-items-center">
                                        <div className="form-check form-check-solid form-switch">
                                            <input

                                                id={`channelSettings[${c}]`}
                                                name={`channelSettings[${c}]`}
                                                className="form-check-input w-45px h-30px" type="checkbox"
                                                checked={formik.values.channelSettings[c] || false}
                                                onChange={formik.handleChange}
                                            />
                                        </div>
                                        <label className='form-label mt-3 ms-2' htmlFor={`channelSettings[${c}]`}>
                                            <span>{Constants.ChannelIcon[c]}</span>
                                            <span className="ms-2">{t(`channels.${NotificationChannel[c].toLowerCase()}`)}</span>
                                        </label>
                                    </div>
                                )
                        }
                    </div>

                    <div className="row mt-4">
                        <div className="col-md-6 mb-10">
                            <label className={`form-label ${formik.values.channelSettings[NotificationChannel.Teams] ? 'required' : ''}`}>
                                {t('notificationCenter.teamsWebhookUrlLabel')}
                            </label>
                            <input
                                type="text"
                                className="form-control form-control-lg form-control-solid"
                                placeholder={t('notificationCenter.teamsWebhookUrlPlaceholder')}
                                {...formik.getFieldProps('teamsWebhookUrl')}
                                disabled={!formik.values.channelSettings[NotificationChannel.Teams]}
                            />
                            {formik.touched.teamsWebhookUrl && formik.errors.teamsWebhookUrl && (
                                <div className="mt-2 text-danger fw-bold">{formik.errors.teamsWebhookUrl}</div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="card-footer d-flex justify-content-end py-6 px-9">
                    <SaveButton type="submit" loading={formik.isSubmitting} onClick={formik.handleSubmit} disabled={!formik.dirty} />
                </div>
            </form>
        </div>
    );
}
